import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Buildings, Phone } from "@phosphor-icons/react";
import { SiteShell, useSite } from "@/components/site/SiteShell";
import { Btn, Eyebrow } from "@/components/site/Blocks";
import { ArrowLink, CountUp, DragRow, Marquee, Rule, SplitText } from "@/components/site/Motion";
import { API } from "@/lib/api";
import { SITE_IMG, STEPS, materialImg } from "@/lib/siteImages";
import { MATERIAL_LINK } from "@/lib/siteMenu";
import { siteImg, usePageHead } from "@/lib/siteContent";
import { useReveal } from "@/lib/useReveal";

const PROJECT_TYPES = [
  "Multi-family residential developments",
  "Luxury rental and condo buildings",
  "Hospitality interiors",
  "Retail environments",
  "Office and co-working spaces",
  "Mixed-use amenity areas",
  "Healthcare and wellness spaces",
  "Developer model units and sales centers",
];

const STONES = [
  { name: "Quartzite", blurb: "The beauty of marble with the durability of granite" },
  { name: "Quartz", blurb: "Low-maintenance, color-consistent and ideal for high-traffic areas" },
  { name: "Granite", blurb: "Naturally bold with enduring strength" },
  { name: "Marble", blurb: "Refined, elegant and timeless", img: SITE_IMG.detail },
  { name: "Porcelain", blurb: "Ultra-thin, ultra-strong and perfect for vertical surfaces", img: SITE_IMG.neolith },
  { name: "Soapstone", blurb: "Smooth, matte and rich with character", img: SITE_IMG.kitchenDark },
];

const PROMISES = [
  {
    title: "Fast turnarounds, without shortcuts",
    body: "We move quickly but never at the cost of quality. With slabs standing on our own racks, in-house CNC fabrication and a fully managed schedule, your project stays on track without gaps, guesswork or compromises.",
  },
  {
    title: "Visual continuity across every unit",
    body: "Color-consistent materials, lot-matched slabs and one set of shop drawings mean unit 2B looks like unit 14A. We hold your material for the whole build so nothing changes halfway through.",
  },
  {
    title: "One team. One roof. No bottlenecks",
    body: "Laser templating, fabrication and installation are all ours. No third-party delays — tighter timelines, better communication and full control over quality from the first template to the last punch list.",
  },
];

const TAGS = ["Developers", "Architects", "Interior designers", "General contractors", "Property managers", "Hospitality", "Retail", "Healthcare"];

const Counter = ({ value, suffix = "", label, testId, text }) => (
  <div data-testid={testId} className="s-reveal py-8 lg:py-0 lg:pl-8">
    <div className="s-num text-5xl sm:text-6xl">
      {typeof value === "number" ? <CountUp value={value} suffix={suffix} /> : <span>{value}{suffix}</span>}
    </div>
    <div className="s-micro mt-3 text-[color:var(--s-mute)]">{label}</div>
    {text && <p className="mt-3 max-w-[16rem] text-sm leading-relaxed text-[color:var(--s-mute)]">{text}</p>}
  </div>
);

export default function SiteTrade() {
  const site = useSite();
  const shop = site?.shop || {};
  const [stock, setStock] = useState(null);

  useEffect(() => {
    fetch(`${API}/public/showroom`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => {
        const items = d.items || [];
        setStock({ pieces: items.reduce((a, i) => a + (i.qty || 1), 0), colors: items.length });
      })
      .catch(() => setStock(null));
  }, []);

  usePageHead({
    title: "Multi-Family & Commercial Projects",
    description:
      "Countertops and stone surfaces for multi-family, hospitality, retail and commercial builds. In-house templating, CNC fabrication and installation on a managed schedule.",
  });
  useReveal([site, stock]);

  return (
    <SiteShell site={site} testId="site-trade" overHero>
      {/* dark hero (blend) */}
      <section className="relative min-h-[70vh] overflow-hidden bg-[color:var(--s-dark)]" data-testid="trade-hero">
        <img
          src={siteImg(site?.config?.trade?.image, SITE_IMG.kitchen2)}
          alt="Multi-family kitchen with stone countertops"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <span className="absolute inset-0 bg-gradient-to-r from-[#1a1a18]/95 via-[#1a1a18]/70 to-[#1a1a18]/30" />
        <span className="absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-[#1a1a18]/80 to-transparent" />
        <span className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#1a1a18] to-transparent" />
        <div className="relative mx-auto flex min-h-[70vh] max-w-[2560px] flex-col justify-end px-6 pb-16 pt-36 lg:px-12 lg:pb-24">
          <div className="s-micro flex items-center gap-2 text-white/70">
            <Buildings size={14} weight="bold" /> Trade · Multi-family &amp; commercial
          </div>
          <h1 className="s-h mt-6 max-w-4xl text-4xl text-white sm:text-5xl lg:text-6xl" data-testid="trade-headline">
            <SplitText text="Multi-Family & Commercial Projects" base={200} />
          </h1>
          <p className="mt-6 max-w-2xl text-base text-white/75 sm:text-lg">
            Premium surfaces. Trusted timelines. Built for scale. Styled for impact.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-6">
            <Btn to="/contact?topic=trade" testId="trade-hero-cta">Partner with us</Btn>
            <ArrowLink to="/inventory" dark testId="trade-hero-inventory">See what is on the racks</ArrowLink>
          </div>
        </div>
      </section>

      {/* intro + counters */}
      <section className="bg-[color:var(--s-bg)] px-6 py-24 sm:py-32 lg:px-12" data-testid="trade-intro">
        <div className="mx-auto max-w-[2560px]">
          <div className="s-reveal">
            <Eyebrow>Boutique precision, large-scale builds</Eyebrow>
            <h2 className="s-hx mt-6 break-words text-5xl sm:text-7xl lg:text-8xl" data-testid="trade-intro-heading">
              Built for <strong>scale.</strong>
            </h2>
            <Rule className="mt-10" />
          </div>
          <div className="mt-12 grid gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-20">
            <div className="s-reveal">
              <p className="text-2xl font-light leading-snug sm:text-3xl">
                Refined stonework that keeps pace with your schedule.
              </p>
              <p className="mt-8 max-w-xl text-base leading-relaxed text-[color:var(--s-mute)]">
                Whether you are developing luxury rentals, multi-unit residences or a standout commercial
                interior, our team delivers stonework that elevates the environment. Every surface is
                selected for performance, designed for visual continuity and fabricated to meet the pace of
                today&apos;s most demanding projects.
              </p>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-[color:var(--s-mute)]">
                We work with builders, architects and interior designers not just because we meet the
                brief, but because we think beyond it — design intelligence, construction fluency and sharp
                problem-solving on every job site, without disruption.
              </p>
              <ArrowLink to="/contact?topic=trade" className="mt-10" testId="trade-intro-link">
                Start a project conversation
              </ArrowLink>
            </div>
            <div className="grid divide-y divide-[color:var(--s-line)] lg:grid-cols-3 lg:divide-x lg:divide-y-0">
              <Counter testId="trade-stat-slabs" value={stock ? stock.pieces : "—"} suffix={stock ? "+" : ""} label="slabs standing in stock today" />
              <Counter testId="trade-stat-colors" value={stock ? stock.colors : "—"} label="colors photographed on the racks" />
              <Counter testId="trade-stat-crew" value={1} label="crew from template to install" />
            </div>
          </div>
        </div>
      </section>

      {/* project types */}
      <section className="border-t border-[color:var(--s-line)] bg-[color:var(--s-surface)]" data-testid="trade-project-types">
        <div className="mx-auto grid max-w-[2560px] gap-12 px-6 py-24 lg:grid-cols-2 lg:gap-20 lg:px-12">
          <div className="s-fig s-soft relative min-h-[40vh] lg:min-h-[64vh]">
            <img src={SITE_IMG.kitchenDark} alt="Commercial kitchen surfaces" className="absolute inset-0 h-full w-full object-cover" />
          </div>
          <div className="s-reveal flex flex-col justify-center">
            <Eyebrow>Custom countertops and stone</Eyebrow>
            <h2 className="s-hx mt-6 text-4xl sm:text-5xl lg:text-6xl">Surfaces for commercial spaces.</h2>
            <Rule className="mt-8" />
            <ul className="mt-6 grid sm:grid-cols-2 sm:gap-x-10">
              {PROJECT_TYPES.map((t, i) => (
                <li key={t} className="flex items-baseline gap-4 border-b border-[color:var(--s-line)] py-4 text-sm">
                  <span className="s-num text-xs text-[color:var(--s-gold)]">{String(i + 1).padStart(2, "0")}</span>
                  {t}
                </li>
              ))}
            </ul>
            <ArrowLink to="/contact?topic=showroom" className="mt-10" testId="trade-showroom-cta">
              Book a showroom visit
            </ArrowLink>
          </div>
        </div>
      </section>

      {/* promises as numbered rows */}
      <section className="bg-[color:var(--s-bg)] px-6 py-24 sm:py-32 lg:px-12" data-testid="trade-promises">
        <div className="mx-auto max-w-[2560px]">
          <div className="s-reveal">
            <Eyebrow>Why builders stay</Eyebrow>
            <h2 className="s-hx mt-6 text-4xl sm:text-5xl lg:text-6xl">Three promises.</h2>
          </div>
          <div className="mt-12 border-t border-[color:var(--s-line)]">
            {PROMISES.map((p, i) => (
              <div
                key={p.title}
                data-testid={`trade-promise-${i + 1}`}
                className="s-reveal grid gap-6 border-b border-[color:var(--s-line)] py-10 lg:grid-cols-[6rem_1fr_1.2fr] lg:gap-12"
              >
                <span className="s-num text-2xl text-[color:var(--s-gold)]">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="s-hx text-2xl sm:text-3xl"><strong>{p.title}</strong></h3>
                <p className="text-base leading-relaxed text-[color:var(--s-mute)]">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* materials — tag marquee + drag row */}
      <section className="border-t border-[color:var(--s-line)] bg-[color:var(--s-surface)] py-24 sm:py-32" data-testid="trade-materials">
        <div className="mx-auto max-w-[2560px] px-6 lg:px-12">
          <div className="s-reveal flex flex-wrap items-end justify-between gap-8">
            <div>
              <Eyebrow>Premium stones selected to perform</Eyebrow>
              <h2 className="s-hx mt-6 text-4xl sm:text-5xl lg:text-6xl">A curated portfolio.</h2>
            </div>
            <p className="max-w-md text-base leading-relaxed text-[color:var(--s-mute)]">
              From natural classics to engineered durability, every slab is chosen for visual impact,
              consistency and long-term value. Most are stocked in-house and ready for production.
            </p>
          </div>
        </div>
        <div className="s-marquee-wrap mt-12 border-y border-[color:var(--s-line)] py-4" data-testid="trade-tags">
          <Marquee speed={40}>
            {TAGS.map((t) => (
              <span key={t} className="s-tag">{t}</span>
            ))}
          </Marquee>
        </div>
        <DragRow className="mt-12 gap-5 px-6 lg:px-12" testId="trade-material-row" label="Drag">
          {STONES.map((s) => (
            <Link
              key={s.name}
              to={MATERIAL_LINK(s.name)}
              data-testid={`trade-material-${s.name.toLowerCase()}`}
              className="group w-[76vw] shrink-0 sm:w-[44vw] lg:w-[28vw] xl:w-[22vw]"
              draggable={false}
            >
              <span className="s-fig s-soft relative block aspect-[4/5]">
                <img src={s.img || materialImg(s.name)} alt={s.name} className="absolute inset-0 h-full w-full object-cover" draggable={false} />
              </span>
              <span className="mt-5 block">
                <span className="s-hx block text-2xl"><strong>{s.name}</strong></span>
                <span className="mt-2 block text-sm leading-relaxed text-[color:var(--s-mute)]">{s.blurb}</span>
              </span>
            </Link>
          ))}
        </DragRow>
      </section>

      {/* how it runs */}
      <section className="bg-[color:var(--s-cream)] px-6 py-24 sm:py-32 lg:px-12" data-testid="trade-how">
        <div className="mx-auto grid max-w-[2560px] gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-20">
          <div className="s-reveal">
            <Eyebrow>How it runs</Eyebrow>
            <h2 className="s-hx mt-6 text-4xl sm:text-5xl lg:text-6xl">Managed in-house, start to finish.</h2>
            <Rule className="mt-8" />
            <ol className="mt-4" data-testid="trade-steps">
              {STEPS.map((s, i) => (
                <li key={s.title} className="grid gap-3 border-b border-[color:var(--s-line)] py-5 sm:grid-cols-[4rem_1fr]" data-testid={`trade-step-${i}`}>
                  <span className="s-num text-sm text-[color:var(--s-gold)]">{String(i + 1).padStart(2, "0")}</span>
                  <span>
                    <span className="block text-lg font-semibold">{s.title}</span>
                    <span className="mt-1 block text-sm leading-relaxed text-[color:var(--s-mute)]">{s.body}</span>
                  </span>
                </li>
              ))}
            </ol>
          </div>
          <div className="s-fig s-soft relative min-h-[40vh] lg:min-h-full">
            <img src={SITE_IMG.fabrication} alt="In-house fabrication" className="absolute inset-0 h-full w-full object-cover" />
          </div>
        </div>
      </section>

      {/* dark CTA */}
      <section className="s-dark-tex s-on-dark px-6 py-24 sm:py-32 lg:px-12" data-testid="trade-cta">
        <div className="mx-auto flex max-w-[2560px] flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
          <div className="s-reveal">
            <Eyebrow dark>Have a project in progress?</Eyebrow>
            <h2 className="s-h mt-6 max-w-3xl text-3xl text-white sm:text-4xl lg:text-5xl">
              Request specs, review timelines or schedule a consultation.
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-6">
            <Btn to="/contact?topic=trade" testId="trade-partner-cta">Partner with us today</Btn>
            {shop.phone && (
              <a href={`tel:${shop.phone_link || shop.phone}`} className="s-ghost-light" data-testid="trade-phone">
                <Phone size={14} weight="bold" /> {shop.phone}
              </a>
            )}
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
